import React, { useCallback, useEffect, useRef, useState } from "react";
import { isShowcaseMode } from "./showcase-db";
import {
  createOfflineMissionRuntime,
  type OfflineMissionRuntime,
} from "./offline-missions-runtime.ts";
import {
  assessOfflineMissionCompletion,
  createOfflineMissionMutation,
  type OfflineChecklistItem,
  type OfflineMissionBundle,
  type OfflineMissionMutation,
} from "./offline-missions.ts";
import { supabase } from "./lib/supabase";

type WorkerOfflineDayProps = {
  profileId: string;
  role: "employee" | "contractor";
  workDate: string;
  onlineMissionCount: number;
};

type OfflineReportPayload = {
  result: "completed" | "issue" | "followup";
  actualHours: number;
  memberHours: Record<string, number>;
  observations: string;
  clientNote: string;
  activityEntries: Array<{
    activity_key: string | null;
    activity_label: string;
    minutes: number;
    notes: string | null;
  }>;
  stockUsages: Array<{
    item_id: string;
    quantity: number;
    notes: string | null;
  }>;
};

type OfflineActivityEntry = {
  activityKey: string;
  activityLabel: string;
  hours: number;
  notes: string;
};

type OfflineStockUsage = {
  itemId: string;
  quantity: number;
  notes: string;
};

function previousActivityEntries(value: unknown): OfflineActivityEntry[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const row = entry as Record<string, unknown>;
    const label = String(row.activity_label ?? "").trim();
    const minutes = Number(row.minutes ?? 0);
    if (!label || !Number.isFinite(minutes)) return [];
    return [{
      activityKey: String(row.activity_key ?? ""),
      activityLabel: label,
      hours: minutes / 60,
      notes: String(row.notes ?? ""),
    }];
  });
}

function previousStockUsages(value: unknown): OfflineStockUsage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const row = entry as Record<string, unknown>;
    const itemId = String(row.item_id ?? "");
    const quantity = Number(row.quantity ?? 0);
    if (!itemId || !Number.isFinite(quantity)) return [];
    return [{ itemId, quantity, notes: String(row.notes ?? "") }];
  });
}

function timeLabel(value: string) {
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function normalizedLabel(value: string) {
  return value.trim().toLocaleLowerCase("fr-FR");
}

function mutationsForProfile(mutations: OfflineMissionMutation[], profileId: string) {
  return mutations.filter((mutation) => mutation.profileId === profileId);
}

function showcaseOfflineMission(input: {
  profileId: string;
  role: "employee" | "contractor";
  workDate: string;
}): OfflineMissionBundle {
  return {
    missionId: `showcase-offline-${input.role}`,
    organizationId: "showcase-organization",
    profileId: input.profileId,
    role: input.role,
    serverVersion: `${input.workDate}T07:00:00.000Z`,
    workDate: input.workDate,
    startsAt: `${input.workDate}T08:00:00+01:00`,
    endsAt: `${input.workDate}T10:00:00+01:00`,
    location: "12 avenue de la Démonstration",
    clientDisplayName: "Mission locale de démonstration",
    contactName: "Contact fictif",
    contactPhone: "06 00 00 00 00",
    instructions: "Contrôler chaque étape puis préparer le compte rendu.",
    checklistEditable: true,
    checklist: [
      {
        id: `showcase-checklist-${input.role}`,
        label: "Contrôler la zone et son état final",
        instructions: "Une photographie de résultat est obligatoire.",
        required: true,
        minimumPhotoCount: 1,
        completed: false,
      },
    ],
    evidence: [],
    reporting: {
      mode: "individual",
      assignedHours: 2,
      members: [],
    },
    activityCatalog: [
      { activityKey: "showcase:preparation", activityLabel: "Préparation et protection" },
      { activityKey: "showcase:execution", activityLabel: "Réalisation de la prestation" },
      { activityKey: "showcase:control", activityLabel: "Contrôle final" },
    ],
    inventoryCatalog: [
      {
        id: "showcase-product",
        name: "Produit de démonstration",
        category: "Produit",
        unit: "L",
        itemKind: "product",
        currentQuantity: 8,
      },
    ],
    downloadedAt: new Date().toISOString(),
    lastSynchronizedAt: null,
  };
}

function OfflineReportEditor({
  mission,
  mutations,
  locallyComplete,
  busy,
  onQueue,
}: {
  mission: OfflineMissionBundle;
  mutations: OfflineMissionMutation[];
  locallyComplete: boolean;
  busy: boolean;
  onQueue: (
    kind: "report_draft_saved" | "completion_requested",
    payload: OfflineReportPayload,
  ) => Promise<void>;
}) {
  const previous = mutations
    .filter((mutation) =>
      ["report_draft_saved", "completion_requested"].includes(mutation.kind),
    )
    .at(-1)?.payload;
  const reporting = mission.reporting ?? {
    mode: "individual" as const,
    assignedHours: 0,
    members: [],
  };
  const [result, setResult] = useState<OfflineReportPayload["result"]>(
    previous?.result === "issue" || previous?.result === "followup"
      ? previous.result
      : "completed",
  );
  const [actualHours, setActualHours] = useState(
    Number(previous?.actualHours ?? reporting.assignedHours ?? 0),
  );
  const [memberHours, setMemberHours] = useState<Record<string, number>>(() => {
    const previousHours = previous?.memberHours;
    if (previousHours && typeof previousHours === "object" && !Array.isArray(previousHours)) {
      return Object.fromEntries(
        Object.entries(previousHours).map(([workerId, hours]) => [workerId, Number(hours)]),
      );
    }
    return Object.fromEntries(
      reporting.members.map((member) => [member.workerId, member.assignedHours]),
    );
  });
  const [observations, setObservations] = useState(String(previous?.observations ?? ""));
  const [clientNote, setClientNote] = useState(String(previous?.clientNote ?? ""));
  const [activityEntries, setActivityEntries] = useState<OfflineActivityEntry[]>(() =>
    previousActivityEntries(previous?.activityEntries),
  );
  const [stockUsages, setStockUsages] = useState<OfflineStockUsage[]>(() =>
    previousStockUsages(previous?.stockUsages),
  );

  function payload(): OfflineReportPayload {
    return {
      result,
      actualHours: Number(actualHours),
      memberHours,
      observations: observations.trim(),
      clientNote: clientNote.trim(),
      activityEntries: activityEntries.map((entry) => ({
        activity_key: entry.activityKey || null,
        activity_label: entry.activityLabel.trim(),
        minutes: Math.round(Number(entry.hours) * 60),
        notes: entry.notes.trim() || null,
      })),
      stockUsages: stockUsages.map((usage) => ({
        item_id: usage.itemId,
        quantity: Number(usage.quantity),
        notes: usage.notes.trim() || null,
      })),
    };
  }

  const invalidHours = reporting.mode === "team_leader"
    ? reporting.members.some((member) => {
        const hours = Number(memberHours[member.workerId]);
        return hours <= 0 || hours > 24;
      })
    : actualHours <= 0 || actualHours > 24;
  const readOnly = reporting.mode === "team_member" || isShowcaseMode;
  const reportedLaborHours = reporting.mode === "team_leader"
    ? reporting.members.reduce(
        (sum, member) => sum + Number(memberHours[member.workerId] ?? 0),
        0,
      )
    : Number(actualHours);
  const activityHours = activityEntries.reduce(
    (sum, entry) => sum + Number(entry.hours || 0),
    0,
  );
  const invalidActivityEntries =
    activityEntries.some(
      (entry) => entry.activityLabel.trim().length < 2 || Number(entry.hours) <= 0,
    ) || activityHours > reportedLaborHours + 0.01;
  const invalidStockUsages = stockUsages.some(
    (usage) => !usage.itemId || Number(usage.quantity) <= 0,
  );
  const invalidCompletionData = invalidHours || invalidActivityEntries || invalidStockUsages;
  const completionQueued = mutations.some(
    (mutation) =>
      mutation.kind === "completion_requested" &&
      !["rejected", "conflict"].includes(mutation.status),
  );

  return (
    <div className="offline-report-editor">
      <div className="section-heading">
        <div>
          <strong>Compte rendu hors connexion</strong>
          <small>
            {reporting.mode === "team_leader"
              ? "Responsable du binôme : renseignez les heures de chaque membre."
              : reporting.mode === "team_member"
                ? "Le responsable du binôme transmet le compte rendu commun."
                : "Le brouillon reste modifiable jusqu’à sa demande de clôture."}
          </small>
        </div>
      </div>
      <div className="offline-report-fields">
        <label>
          Résultat
          <select
            value={result}
            disabled={readOnly || busy}
            onChange={(event) => setResult(event.target.value as OfflineReportPayload["result"])}
          >
            <option value="completed">Réalisée</option>
            <option value="issue">Problème rencontré</option>
            <option value="followup">Action complémentaire nécessaire</option>
          </select>
        </label>
        {reporting.mode === "team_leader" ? reporting.members.map((member) => (
          <label key={member.workerId}>
            Heures · {member.displayName}
            <input
              type="number"
              min="0.01"
              max="24"
              step="0.25"
              value={memberHours[member.workerId] ?? ""}
              disabled={readOnly || busy}
              onChange={(event) => setMemberHours((current) => ({
                ...current,
                [member.workerId]: Number(event.target.value),
              }))}
            />
          </label>
        )) : (
          <label>
            Heures réellement effectuées
            <input
              type="number"
              min="0.01"
              max="24"
              step="0.25"
              value={actualHours || ""}
              disabled={readOnly || busy}
              onChange={(event) => setActualHours(Number(event.target.value))}
            />
          </label>
        )}
        <label className="wide">
          Observations internes
          <textarea
            rows={3}
            value={observations}
            disabled={readOnly || busy}
            onChange={(event) => setObservations(event.target.value)}
          />
        </label>
        <label className="wide">
          Note visible par le client après validation
          <textarea
            rows={3}
            value={clientNote}
            disabled={readOnly || busy}
            onChange={(event) => setClientNote(event.target.value)}
          />
        </label>
      </div>
      <details className="offline-report-detail" open>
        <summary>
          Temps par grande étape <small>facultatif</small>
        </summary>
        <p>
          Le total peut rester partiel, mais ne doit jamais dépasser les heures humaines
          déclarées.
        </p>
        <datalist id={`offline-activity-${mission.missionId}`}>
          {(mission.activityCatalog ?? []).map((activity) => (
            <option key={activity.activityKey} value={activity.activityLabel} />
          ))}
        </datalist>
        {activityEntries.map((entry, index) => (
          <div className="offline-report-row" key={`${index}-${entry.activityKey}`}>
            <label>
              Étape
              <input
                list={`offline-activity-${mission.missionId}`}
                value={entry.activityLabel}
                disabled={readOnly || busy}
                onChange={(event) => {
                  const activityLabel = event.target.value;
                  const known = (mission.activityCatalog ?? []).find(
                    (activity) =>
                      normalizedLabel(activity.activityLabel) === normalizedLabel(activityLabel),
                  );
                  setActivityEntries((current) => current.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, activityLabel, activityKey: known?.activityKey ?? "" }
                      : item,
                  ));
                }}
              />
            </label>
            <label>
              Temps humain (h)
              <input
                type="number"
                min="0.1"
                max="48"
                step="0.1"
                value={entry.hours || ""}
                disabled={readOnly || busy}
                onChange={(event) => setActivityEntries((current) =>
                  current.map((item, itemIndex) => itemIndex === index
                    ? { ...item, hours: Number(event.target.value) }
                    : item),
                )}
              />
            </label>
            <label>
              Précision
              <input
                value={entry.notes}
                disabled={readOnly || busy}
                onChange={(event) => setActivityEntries((current) =>
                  current.map((item, itemIndex) => itemIndex === index
                    ? { ...item, notes: event.target.value }
                    : item),
                )}
              />
            </label>
            <button
              type="button"
              className="danger compact"
              disabled={readOnly || busy}
              onClick={() => setActivityEntries((current) =>
                current.filter((_item, itemIndex) => itemIndex !== index),
              )}
            >
              Retirer
            </button>
          </div>
        ))}
        <div className="offline-report-totals">
          <span>Temps détaillé <strong>{activityHours.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong></span>
          <span>Non détaillé <strong>{Math.max(0, reportedLaborHours - activityHours).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h</strong></span>
        </div>
        {activityHours > reportedLaborHours + 0.01 && (
          <p className="notice error">Le temps détaillé dépasse le total humain déclaré.</p>
        )}
        <button
          type="button"
          className="secondary compact"
          disabled={readOnly || busy || activityEntries.length >= 20}
          onClick={() => setActivityEntries((current) => [...current, {
            activityKey: "",
            activityLabel: "",
            hours: Math.max(0.1, Number((reportedLaborHours - activityHours).toFixed(2)) || 0.1),
            notes: "",
          }])}
        >
          Ajouter une étape
        </button>
      </details>
      <details className="offline-report-detail" open>
        <summary>
          Produits déposés ou consommés <small>quantités uniquement</small>
        </summary>
        <p>
          Aucun prix ni choix de refacturation n’est visible ici : l’administration les
          contrôle après transmission.
        </p>
        {stockUsages.map((usage, index) => (
          <div className="offline-report-row" key={`${index}-${usage.itemId}`}>
            <label>
              Produit
              <select
                value={usage.itemId}
                disabled={readOnly || busy}
                onChange={(event) => setStockUsages((current) =>
                  current.map((item, itemIndex) => itemIndex === index
                    ? { ...item, itemId: event.target.value }
                    : item),
                )}
              >
                <option value="">Choisir</option>
                {(mission.inventoryCatalog ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} · {item.unit}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Quantité
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={usage.quantity || ""}
                disabled={readOnly || busy}
                onChange={(event) => setStockUsages((current) =>
                  current.map((item, itemIndex) => itemIndex === index
                    ? { ...item, quantity: Number(event.target.value) }
                    : item),
                )}
              />
            </label>
            <label>
              Précision
              <input
                value={usage.notes}
                disabled={readOnly || busy}
                onChange={(event) => setStockUsages((current) =>
                  current.map((item, itemIndex) => itemIndex === index
                    ? { ...item, notes: event.target.value }
                    : item),
                )}
              />
            </label>
            <button
              type="button"
              className="danger compact"
              disabled={readOnly || busy}
              onClick={() => setStockUsages((current) =>
                current.filter((_item, itemIndex) => itemIndex !== index),
              )}
            >
              Retirer
            </button>
          </div>
        ))}
        {!stockUsages.length && <p>Aucun produit renseigné pour cette mission.</p>}
        <button
          type="button"
          className="secondary compact"
          disabled={readOnly || busy || !(mission.inventoryCatalog ?? []).length}
          onClick={() => setStockUsages((current) => [...current, {
            itemId: mission.inventoryCatalog?.[0]?.id ?? "",
            quantity: 1,
            notes: "",
          }])}
        >
          Ajouter un produit
        </button>
      </details>
      <div className="offline-day-actions">
        <button
          type="button"
          className="secondary"
          disabled={readOnly || busy || invalidHours}
          onClick={() => void onQueue("report_draft_saved", payload())}
        >
          Enregistrer le brouillon local
        </button>
        <button
          type="button"
          disabled={
            readOnly ||
            busy ||
            invalidCompletionData ||
            !locallyComplete ||
            completionQueued
          }
          onClick={() => void onQueue("completion_requested", payload())}
        >
          {completionQueued ? "Clôture déjà demandée" : "Demander la clôture"}
        </button>
      </div>
      <small>
        La demande crée un compte rendu en attente de contrôle. Elle ne valide ni la facture
        client ni la facture du sous-traitant.
      </small>
    </div>
  );
}

export function WorkerOfflineDay({
  profileId,
  role,
  workDate,
  onlineMissionCount,
}: WorkerOfflineDayProps) {
  const [runtime, setRuntime] = useState<OfflineMissionRuntime | null>(null);
  const [missions, setMissions] = useState<OfflineMissionBundle[]>([]);
  const [pending, setPending] = useState<OfflineMissionMutation[]>([]);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [busy, setBusy] = useState<"prepare" | "sync" | "">("");
  const [expanded, setExpanded] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const preparedKeyRef = useRef("");

  const refreshLocalState = useCallback(
    async (activeRuntime: OfflineMissionRuntime) => {
      const [storedMissions, mutations] = await Promise.all([
        activeRuntime.store.listMissionBundles(profileId, workDate),
        activeRuntime.store.listMutations(),
      ]);
      setMissions(storedMissions);
      setPending(mutationsForProfile(mutations, profileId));
    },
    [profileId, workDate],
  );

  useEffect(() => {
    let active = true;
    if (!supabase) return () => { active = false; };
    createOfflineMissionRuntime(supabase)
      .then(async (created) => {
        if (!active) return;
        setRuntime(created);
        await refreshLocalState(created);
      })
      .catch((error) => {
        if (active) {
          setMessage(
            error instanceof Error
              ? error.message
              : "Le stockage hors connexion n’est pas disponible sur cet appareil.",
          );
        }
      });
    return () => { active = false; };
  }, [refreshLocalState]);

  useEffect(() => {
    const updateOnline = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);

  const synchronize = useCallback(async () => {
    if (!runtime || !online || isShowcaseMode) return;
    setBusy("sync");
    setMessage("");
    try {
      const results = await runtime.engine.synchronizePending();
      await refreshLocalState(runtime);
      const conflict = results.find((item) => item.status === "conflict");
      const rejected = results.find((item) => item.status === "rejected");
      setMessage(
        conflict?.failureReason ||
          rejected?.failureReason ||
          (results.length
            ? `${results.filter((item) => item.status === "acknowledged").length} modification(s) synchronisée(s).`
            : "Aucune modification en attente."),
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? `Synchronisation interrompue : ${error.message}`
          : "Synchronisation interrompue. Les données restent conservées sur cet appareil.",
      );
    } finally {
      setBusy("");
    }
  }, [online, refreshLocalState, runtime]);

  const pendingSynchronization = pending.some((item) => item.status === "pending");
  useEffect(() => {
    if (online && runtime && pendingSynchronization) void synchronize();
  }, [online, pendingSynchronization, runtime, synchronize]);

  useEffect(() => {
    if (!runtime || !online || isShowcaseMode) return;
    const preparationKey = `${profileId}:${workDate}`;
    if (preparedKeyRef.current === preparationKey) return;
    preparedKeyRef.current = preparationKey;
    void runtime.engine.downloadDay({ profileId, role, workDate })
      .then(() => refreshLocalState(runtime))
      .catch((error) => {
        preparedKeyRef.current = "";
        setMessage(
          error instanceof Error
            ? `Mise à jour de la journée interrompue : ${error.message}`
            : "La journée n’a pas pu être actualisée. Les données déjà présentes restent disponibles.",
        );
      });
  }, [online, profileId, refreshLocalState, role, runtime, workDate]);

  useEffect(() => {
    if (!online) setExpanded(true);
  }, [online]);

  async function prepareDay() {
    if (!runtime || !online || isShowcaseMode) return;
    setBusy("prepare");
    setMessage("");
    try {
      const downloaded = await runtime.engine.downloadDay({
        profileId,
        role,
        workDate,
      });
      await refreshLocalState(runtime);
      setExpanded(true);
      setMessage(
        `${downloaded.length} mission(s) enregistrée(s) sur cet appareil pour la journée.`,
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? `Préparation impossible : ${error.message}`
          : "Préparation impossible. Les missions déjà enregistrées restent disponibles.",
      );
    } finally {
      setBusy("");
    }
  }

  function missionMutations(missionId: string) {
    return pending.filter((mutation) => mutation.missionId === missionId);
  }

  function effectiveItemCompleted(
    mission: OfflineMissionBundle,
    item: OfflineChecklistItem,
  ) {
    const latest = missionMutations(mission.missionId)
      .filter(
        (mutation) =>
          mutation.kind === "checklist_item_set" &&
          mutation.payload.checklistItemId === item.id &&
          !["rejected", "conflict"].includes(mutation.status),
      )
      .at(-1);
    return latest ? Boolean(latest.payload.completed) : item.completed;
  }

  function effectivePhotoCount(mission: OfflineMissionBundle, itemId: string) {
    const remoteIds = new Set(
      mission.evidence
        .filter((evidence) => evidence.checklistItemId === itemId)
        .map((evidence) => evidence.id),
    );
    missionMutations(mission.missionId).forEach((mutation) => {
      if (["rejected", "conflict"].includes(mutation.status)) return;
      if (mutation.payload.checklistItemId !== itemId) return;
      const id = String(mutation.payload.evidenceId ?? mutation.id);
      if (mutation.kind === "evidence_added") remoteIds.add(id);
      if (mutation.kind === "evidence_removed") remoteIds.delete(id);
    });
    return remoteIds.size;
  }

  async function queueChecklistToggle(
    mission: OfflineMissionBundle,
    item: OfflineChecklistItem,
  ) {
    if (!runtime || busy) return;
    if (mission.checklistEditable === false || mission.reporting?.mode === "team_member") {
      setMessage("Cette checklist est pilotée par le responsable de la mission.");
      return;
    }
    setBusy("sync");
    setMessage("");
    try {
      const mutation = createOfflineMissionMutation({
        id: crypto.randomUUID(),
        mission,
        kind: "checklist_item_set",
        payload: {
          checklistItemId: item.id,
          completed: !effectiveItemCompleted(mission, item),
        },
        createdAt: new Date().toISOString(),
      });
      await runtime.queueMutation(mutation);
      await refreshLocalState(runtime);
      setMessage(
        online
          ? "Étape enregistrée localement. La synchronisation va confirmer le contrôle serveur."
          : "Étape conservée hors connexion sur cet appareil.",
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "L’étape n’a pas pu être conservée.");
    } finally {
      setBusy("");
    }
  }

  async function queueChecklistPhoto(
    mission: OfflineMissionBundle,
    item: OfflineChecklistItem,
    file: File,
  ) {
    if (!runtime || busy) return;
    if (mission.checklistEditable === false || mission.reporting?.mode === "team_member") {
      setMessage("Les preuves de cette mission sont pilotées par le responsable de la mission.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setMessage("La preuve doit être une photo JPEG, PNG ou WebP.");
      return;
    }
    if (!file.size || file.size > 10 * 1024 * 1024) {
      setMessage("La photo doit peser entre 1 octet et 10 Mo.");
      return;
    }
    setBusy("sync");
    setMessage("");
    try {
      const id = crypto.randomUUID();
      const capturedAt = new Date().toISOString();
      const mutation = createOfflineMissionMutation({
        id,
        mission,
        kind: "evidence_added",
        payload: {
          checklistItemId: item.id,
          evidenceId: id,
          mimeType: file.type,
          originalName: file.name,
          capturedAt,
          localUri: `offline-private:${id}`,
        },
        createdAt: capturedAt,
      });
      await runtime.queueMutation(mutation, file);
      await refreshLocalState(runtime);
      setMessage(
        online
          ? "Photo sécurisée localement. La synchronisation va confirmer son enregistrement."
          : "Photo conservée hors connexion dans l’espace privé de l’application.",
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "La photo n’a pas pu être conservée.");
    } finally {
      setBusy("");
    }
  }

  async function queueReportMutation(
    mission: OfflineMissionBundle,
    kind: "report_draft_saved" | "completion_requested",
    payload: OfflineReportPayload,
  ) {
    if (!runtime || busy) return;
    if (mission.reporting?.mode === "team_member") {
      setMessage("Le responsable du binôme transmet le compte rendu commun.");
      return;
    }
    if (kind === "completion_requested") {
      const assessment = assessOfflineMissionCompletion(
        mission,
        missionMutations(mission.missionId),
      );
      if (!assessment.canQueueCompletion) {
        setMessage("La checklist et toutes les preuves obligatoires doivent être complètes avant la clôture.");
        return;
      }
    }
    setBusy("sync");
    setMessage("");
    try {
      const mutation = createOfflineMissionMutation({
        id: crypto.randomUUID(),
        mission,
        kind,
        payload: { ...payload },
        createdAt: new Date().toISOString(),
      });
      await runtime.queueMutation(mutation);
      await refreshLocalState(runtime);
      setMessage(
        kind === "report_draft_saved"
          ? online
            ? "Brouillon conservé sur cet appareil. Synchronisez pour le sauvegarder sur le serveur."
            : "Brouillon conservé hors connexion sur cet appareil."
          : online
            ? "Demande de clôture conservée. Synchronisez pour lancer le contrôle serveur."
            : "Demande de clôture conservée hors connexion. Elle sera contrôlée après synchronisation.",
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Le compte rendu n’a pas pu être conservé sur cet appareil.",
      );
    } finally {
      setBusy("");
    }
  }

  const unresolved = pending.filter((item) => item.status !== "acknowledged");
  const conflicts = unresolved.filter((item) =>
    ["conflict", "rejected"].includes(item.status),
  ).length;
  const displayedMissions = missions.length || !isShowcaseMode
    ? missions
    : [showcaseOfflineMission({ profileId, role, workDate })];
  const technicalFailure = /impossible|interrompue|conflit|refus|n’a pas pu/i.test(message);
  const showContinuityPanel = !isShowcaseMode && (
    !online || busy !== "" || unresolved.length > 0 || conflicts > 0 || technicalFailure
  );

  if (!showContinuityPanel) return null;

  return (
    <section className="panel worker-offline-day" data-testid="worker-offline-day">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Continuité automatique</span>
          <h2>{!online ? "Connexion indisponible" : conflicts ? "Une saisie doit être vérifiée" : "Transmission en cours"}</h2>
          <p className="section-intro">
            {!online
              ? "Continuez vos missions normalement. Vos validations et photos restent protégées sur cet appareil et partiront automatiquement au retour du réseau."
              : conflicts
                ? "Vos données sont conservées. Ouvrez le détail uniquement pour résoudre les éléments signalés."
                : "Vos dernières validations sont transmises automatiquement. Vous n’avez rien à déclencher."}
          </p>
        </div>
        <span className={`offline-network-state ${online ? "online" : "offline"}`}>
          {online ? busy === "sync" ? "Synchronisation…" : "Connexion rétablie" : "Hors connexion"}
        </span>
      </div>
      {(conflicts > 0 || !online) && !!displayedMissions.length && (
        <div className="offline-day-actions">
          <button type="button" className="secondary" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Masquer le détail" : "Voir mes missions enregistrées"}
          </button>
        </div>
      )}
      {message && (
        <p className={`notice ${technicalFailure ? "error" : "success"}`}>
          {message}
        </p>
      )}
      {!online && !displayedMissions.length && (
        <p className="notice info">Aucune mission n’avait encore été enregistrée sur cet appareil. Reconnectez-vous pour actualiser votre agenda.</p>
      )}

      {expanded && displayedMissions.length > 0 && (
        <div className="offline-mission-list">
          {displayedMissions.map((mission) => {
            const completed = mission.checklist.filter((item) =>
              effectiveItemCompleted(mission, item),
            ).length;
            return (
              <article key={mission.missionId} className={selectedMissionId === mission.missionId ? "selected" : ""}>
                <button
                  type="button"
                  className="offline-mission-summary"
                  onClick={() => setSelectedMissionId(
                    selectedMissionId === mission.missionId ? null : mission.missionId,
                  )}
                >
                  <span>
                  <time>{timeLabel(mission.startsAt)} – {timeLabel(mission.endsAt)}</time>
                  <strong>{mission.clientDisplayName || "Mission"}</strong>
                  <em>{mission.location}</em>
                  </span>
                  <small>
                  {mission.checklist.length
                    ? `${completed}/${mission.checklist.length} étape(s) cochée(s)`
                    : "Aucune checklist associée"}
                  </small>
                </button>
                {selectedMissionId === mission.missionId && (
                  <div className="offline-checklist-editor">
                    {(mission.checklistEditable === false || mission.reporting?.mode === "team_member") && (
                      <p className="notice info">
                        Consultation uniquement : le responsable de la mission pilote la checklist,
                        les preuves et le compte rendu commun.
                      </p>
                    )}
                    {mission.checklist.length ? mission.checklist.map((item) => {
                      const completedNow = effectiveItemCompleted(mission, item);
                      const photoCount = effectivePhotoCount(mission, item.id);
                      return (
                        <div key={item.id} className={completedNow ? "complete" : ""}>
                          <label>
                            <input
                              type="checkbox"
                              checked={completedNow}
                              disabled={
                                busy !== "" ||
                                isShowcaseMode ||
                                mission.checklistEditable === false ||
                                mission.reporting?.mode === "team_member"
                              }
                              onChange={() => void queueChecklistToggle(mission, item)}
                            />
                            <span>
                              <strong>{item.label}</strong>
                              {item.instructions && <small>{item.instructions}</small>}
                            </span>
                          </label>
                          <div>
                            <span>
                              {photoCount}/{item.minimumPhotoCount} photo(s)
                              {item.required ? " · obligatoire" : " · facultatif"}
                            </span>
                            <label className="offline-photo-button">
                              Ajouter une photo
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                capture="environment"
                                disabled={
                                  busy !== "" ||
                                  isShowcaseMode ||
                                  mission.checklistEditable === false ||
                                  mission.reporting?.mode === "team_member"
                                }
                                onChange={(event) => {
                                  const file = event.currentTarget.files?.[0];
                                  if (file) void queueChecklistPhoto(mission, item, file);
                                  event.currentTarget.value = "";
                                }}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    }) : (
                      <p>Aucune checklist n’est associée à cette mission. L’administration doit en affecter une avant clôture si la prestation exige des contrôles.</p>
                    )}
                    {(() => {
                      const assessment = assessOfflineMissionCompletion(
                        mission,
                        missionMutations(mission.missionId),
                      );
                      return (
                        <>
                          <p className={`offline-completion-state ${assessment.locallyComplete ? "ready" : "incomplete"}`}>
                            {assessment.locallyComplete
                              ? "Complète sur l’appareil · synchronisation et validation serveur encore obligatoires."
                              : `${assessment.missingRequiredItemIds.length} étape(s) et ${assessment.missingPhotoItemIds.length} exigence(s) photo restent à compléter.`}
                          </p>
                          <OfflineReportEditor
                            key={mission.missionId}
                            mission={mission}
                            mutations={missionMutations(mission.missionId)}
                            locallyComplete={assessment.locallyComplete}
                            busy={busy !== ""}
                            onQueue={(kind, payload) =>
                              queueReportMutation(mission, kind, payload)
                            }
                          />
                        </>
                      );
                    })()}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      <small className="offline-safety-note">
        Une mission conservée localement n’est ni terminée ni facturable tant que les étapes,
        photos et contrôles serveur ne sont pas tous confirmés.
      </small>
    </section>
  );
}
